import {Locale} from '../Locale';
import {SchoolBaseDto, SchoolGrade} from '../location/School';
import {Role} from './Role';
import {PaymentDetails, PaymentStatus} from '../Payment';

export class User {

  public id!: string;

  public firstName!: string;

  public lastName!: string;

  public email!: string;

  public username?: string;

  public phone?: string;

  public emailVerified: boolean = false;

  public status?: UserStatusType;

  public contactPerson: boolean = false;

  public picture?: string;

  public locale?: Locale;

  public provider?: string;
  public poolId?: string;

  public schoolType?: string;
  public schoolGrade?: SchoolGrade;
  public schoolId?: string;
  public schoolName?: string;

  public enrolledAt?: Date;

  public region?: string;

  public paymentId?: string;
  public paymentPrice?: number;
  public paymentSessionId?: string;
  public paymentInvoiceId?: string;
  public paymentPageUrl?: string;
  public paymentStatus?: PaymentStatus;
  public paymentAt?: Date;
  public paymentName?: string;
  public paymentNotConfigured?: boolean;

  public teacher_id?: string;
  public teacher_first_name?: string;
  public teacher_last_name?: string;

  constructor() {
  }

  static of(id: string): User {
    const user = new User();

    user.id = id;

    return user;
  }

  public getAvatarLetters(): string {
    let avatarLetters = (this.firstName ? this.firstName[0] : '') + (this.lastName ? this.lastName[0] : '');

    if (!avatarLetters) {
      avatarLetters = 'U';
    }

    return avatarLetters.toUpperCase();
  }

  public static parse(dto: UserDto): User {
    const user = User.of(dto.id);

    user.email = dto.email;
    user.emailVerified = dto.email_verified || false;
    user.username = dto.username || dto.preferred_username;
    user.phone = dto.phone;
    user.firstName = dto.first_name || '';
    user.lastName = dto.last_name || '';
    user.schoolId = dto.school;
    user.schoolGrade = dto.grade || dto.school_grade;
    user.region = dto.region;
    user.contactPerson = dto.contact_host || false;
    user.status = dto.status;
    user.locale = dto.locale;
    user.poolId = dto.pool_id;

    return user;
  }

  public static parseEnrolled(dto: EnrolledUserDto): User {
    const user = this.parse(dto);

    if (dto.email_verified) user.emailVerified = dto.email_verified;
    user.status = dto.status || 'UNKNOWN';

    if (dto.school_id) user.schoolId = dto.school_id;
    if (dto.school_name) user.schoolName = dto.school_name;
    if (dto.school_grade) user.schoolGrade = dto.school_grade;
    if (dto.enrolled_at) user.enrolledAt = new Date(dto.enrolled_at);

    if (dto.payment_id) user.paymentId = dto.payment_id;
    if (dto.payment_price) user.paymentPrice = dto.payment_price;
    if (dto.payment_session_id) user.paymentSessionId = dto.payment_session_id;
    if (dto.payment_invoice_id) user.paymentInvoiceId = dto.payment_invoice_id;
    if (dto.payment_page_url) user.paymentPageUrl = dto.payment_page_url;
    if (dto.payment_status) user.paymentStatus = dto.payment_status;
    if (dto.payment_at) user.paymentAt = new Date(dto.payment_at);
    if (dto.payment_not_configured) user.paymentNotConfigured = dto.payment_not_configured;
    if (dto.name) user.paymentName = dto.name;

    if (dto.teacher_id) user.teacher_id = dto.teacher_id;
    if (dto.teacher_first_name) user.teacher_first_name = dto.teacher_first_name;
    if (dto.teacher_last_name) user.teacher_last_name = dto.teacher_last_name;

    return user;
  }
}

export interface UserDto {

  id: string;

  first_name?: string;

  last_name?: string;

  email: string;

  email_verified?: boolean;

  username?: string;
  preferred_username?: string;

  phone?: string;

  contact_host?: boolean;

  school?: string;

  grade?: SchoolGrade;

  school_grade?: SchoolGrade;

  region?: string;

  status?: UserStatusType;

  locale?: Locale;

  pool_id?: string;
}

export interface EnrolledUserDto extends UserDto, PaymentDetails, TeacherBaseDto {

  email_verified?: boolean;

  status?: UserStatusType;

  school_id?: string;

  school_name?: string;

  school_grade?: SchoolGrade;

  enrolled_at?: number;

  name?: string;
}

export interface TeacherBaseDto {
  teacher_id?: string;
  teacher_first_name?: string;
  teacher_last_name?: string;
}

export interface Teacher {
  teacher_id: string;
  teacher_first_name?: string;
  teacher_last_name?: string;
}

export interface InviteUserDto {
  email?: string;
  role: Role;

  first_name?: string;
  last_name?: string;
  locale?: string;

  competition_id?: string;

  school_id?: string;
  school_grade?: string;
  region?: string;

  resend?: boolean;
  resend_tenant_domain?: string;
  resend_tenant_name?: string;
  resend_locale?: Locale;
}

export interface UpdateUserDto {
  propagate: boolean;

  email?: string;
  phone?: string;

  first_name?: string;
  last_name?: string;

  region?: string;
  school_id?: string;
  school_grade?: SchoolGrade;
}

export type UserStatusType =
  'UNCONFIRMED'
  | 'CONFIRMED'
  | 'ARCHIVED'
  | 'COMPROMISED'
  | 'UNKNOWN'
  | 'RESET_REQUIRED'
  | 'FORCE_CHANGE_PASSWORD'
  | string;

export function localizeUserStatus(status: UserStatusType): string {
  let value: string = '';

  switch (status) {
    case 'UNCONFIRMED':
      value = $localize`:General|User Status Unconfirmed title@@general-user-status-unconfirmed-title:Unconfirmed`;
      break;
    case 'CONFIRMED':
      value = $localize`:General|User Status Confirmed title@@general-user-status-confirmed-title:Confirmed`;
      break;
    case 'ARCHIVED':
      value = $localize`:General|User Status Archived title@@general-user-status-archived-title:Archived`;
      break;
    case 'COMPROMISED':
      value = $localize`:General|User Status Compromised title@@general-user-status-compromised-title:Compromised`;
      break;
    case 'UNKNOWN':
      value = $localize`:General|User Status Unknown title@@general-user-status-unknown-title:Unknown`;
      break;
    case 'RESET_REQUIRED':
      value = $localize`:General|User Status Reset Required title@@general-user-status-reset_required-title:Password Reset Required`;
      break;
    case 'FORCE_CHANGE_PASSWORD':
      value = $localize`:General|User Status Force Change Password title@@general-user-status-force_change_password-title:Not Active`;
      break;
  }

  return value;
}

export interface UserExistsBaseDto {
  id?: string;
  email_verified?: boolean;
  status: UserStatusType;
}

export interface UserExistsDto extends UserExistsBaseDto {
  school_id?: string;
  school_grade?: SchoolGrade;
  first_name?: string;
  last_name?: string;
}

export interface EnrolData {
  user_id: string;
  role: Role;
  pay_later?: boolean;
  free_enrol?: boolean;
}

export interface EnrolDto {
  data?: EnrolData[];
  school?: SchoolBaseDto;
  region_id?: string;
  free_enrol?: boolean;
}

export interface EnrolRequestDto {
  user_id: string;
  role: Role;
  approve: boolean;
}

export interface UserMigrationResult {
  retry_auth: boolean;
  status: UserStatusType;
}

export interface ChangePasswordResult {
  username: string;
  password: string;
}

export enum UserImportIssue {
  contest_required = 'contest_required',
  enrol_failed = 'enrol_failed',
  email_incorrect = 'email_incorrect',
  email_required = 'email_required',
  incorrect_grade = 'incorrect_grade',
  incorrect_school = 'incorrect_school',
}

export function localizeUserImportIssue(issue: UserImportIssue): string {
  let value: string = '';

  switch (issue) {
    case UserImportIssue.contest_required:
      value = $localize`:General|User Import Issue contest_required title@@general-user-import-issue-contest_required-title:Contest is Required`;
      break;
    case UserImportIssue.enrol_failed:
      value = $localize`:General|User Import Issue enrol_failed title@@general-user-import-issue-enrol_failed-title:Enrollment Failed`;
      break;
    case UserImportIssue.email_incorrect:
      value = $localize`:General|User Import Issue email_incorrect title@@general-user-import-issue-email_incorrect-title:Email is Incorrect`;
      break;
    case UserImportIssue.email_required:
      value = $localize`:General|User Import Issue email_required title@@general-user-import-issue-email_required-title:Email is Required`;
      break;
    case UserImportIssue.incorrect_grade:
      value = $localize`:General|User Import Issue incorrect_grade title@@general-user-import-issue-incorrect_grade-title:School Grade is Incorrect`;
      break;
    case UserImportIssue.incorrect_school:
      value = $localize`:General|User Import Issue incorrect_school title@@general-user-import-issue-incorrect_school-title:School is Incorrect`;
      break;
  }

  return value;
}

export interface UserImportResultDto {
  users: UserDto[];
  issues?: {[key in UserImportIssue]?: UserDto[]};
}

export interface UserImportResult {
  users: User[];
  issues: {[key in UserImportIssue]?: User[]};
}
